import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ApiService } from '@core/api/angular';
import { getVicnamesRadiusFromCoordinates } from '@core/api/angular/operations/get-vicnames-radius-from-coordinates';
import { normalSearchVicnamesFeaturesOptions } from '@core/api/angular/operations/normal-search-vicnames-features-options';
import { MAP_ROOT } from '@lassi/config';
import { convertDMSStringToDecimal } from '@lassi/util';
import { latLng, LatLng, Marker, Polygon } from 'leaflet';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  Observable,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { MapService } from './map';
import { VicnamesPolygonSearchMode } from './map/map-mode/draw.mode';
import { createDuplicateSearchMarker } from './map/map.model';
import { IVicnamesRoadDuplicateSearchForm } from './models';
import { IVicnamesFeatureDuplicateSearchForm } from './models/vicnames-feature.model';
import { DMSPipe } from './pipes/coordinates.pipe';
import { RouteUtilsService } from './route-utils.service';
import { SelectFromMapService } from './select-from-map.service';
import { ToastService } from './toast/toast.service';

export enum VicnamesSearchType {
  FEATURE_SEARCH = 'features',
  ROAD_SEARCH = 'roads',
}

type DuplicateSearchCoordinatesForm = Pick<
  IVicnamesFeatureDuplicateSearchForm | IVicnamesRoadDuplicateSearchForm,
  'latitude' | 'longitude'
>;

@Injectable({
  providedIn: 'root',
})
export class VicnamesSearchService {
  private readonly mapService = inject(MapService);
  private readonly selectFromMapService = inject(SelectFromMapService);
  private readonly router = inject(Router);
  private readonly apiService = inject(ApiService);
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly mapRoot = inject(MAP_ROOT);
  private currentMarker: Marker | null = null;

  readonly POLYGON_SEARCH_ALLOWED_THRESHOLD = 10_000;

  readonly VICNAMES_SEARCH_RESULT_KEY = 'vicnames-search';

  getFeatureSearchOptions() {
    return this.apiService.invoke(normalSearchVicnamesFeaturesOptions);
  }

  public startGetCoordinateFromMap(
    formValues:
      | IVicnamesFeatureDuplicateSearchForm
      | IVicnamesRoadDuplicateSearchForm,
    searchType: VicnamesSearchType,
  ) {
    this.selectFromMapService
      .selectFromMap()
      .pipe(
        switchMap((e) => {
          const dmsPipe = new DMSPipe();

          // convert negative lat to positive because we only target australia
          const latitude = dmsPipe.transform(
            Math.abs(e.latlng.lat).toString().slice(0, 16),
          );
          const longitude = dmsPipe.transform(
            e.latlng.lng.toString().slice(0, 16),
          );
          return this.apiService
            .invoke(getVicnamesRadiusFromCoordinates, {
              latitude,
              longitude,
            })
            .pipe(
              switchMap((data) =>
                this.goBackToCoordinateSearchForm(
                  {
                    ...formValues,
                    latitude,
                    longitude,
                    radius: data.radius,
                  },
                  searchType,
                ),
              ),
              catchError((error) => {
                console.error(error);
                this.toast.showErrorMessage(
                  'Could not get the radius for this location',
                );
                return this.goBackToCoordinateSearchForm(
                  formValues,
                  searchType,
                );
              }),
            );
        }),
        finalize(() => this.mapService.resetMapMode()),
      )
      .subscribe();
  }

  private async goBackToCoordinateSearchForm(
    formValues:
      | IVicnamesFeatureDuplicateSearchForm
      | IVicnamesRoadDuplicateSearchForm,
    searchType: VicnamesSearchType,
  ) {
    if (searchType === VicnamesSearchType.FEATURE_SEARCH) {
      return await this.router.navigate(
        [
          this.mapRoot,
          {
            outlets: {
              [RouteUtilsService.MAP_MENU_OUTLET_NAME]: [
                RouteUtilsService.MAP_MENU_ROOT_PATH,
                'vicnames',
                'feature-search',
                'duplicates',
              ],
            },
          },
        ],
        {
          queryParamsHandling: 'merge',
          queryParams: {
            duplicateFeatureSearchForm: JSON.stringify(formValues),
          },
        },
      );
    } else if (searchType === VicnamesSearchType.ROAD_SEARCH) {
      return await this.router.navigate(
        [
          this.mapRoot,
          {
            outlets: {
              [RouteUtilsService.MAP_MENU_OUTLET_NAME]: [
                RouteUtilsService.MAP_MENU_ROOT_PATH,
                'vicnames',
                'road-search',
                'duplicates',
              ],
            },
          },
        ],
        {
          queryParamsHandling: 'merge',
          queryParams: {
            duplicateRoadSearchForm: JSON.stringify(formValues),
          },
        },
      );
    } else {
      return Promise.reject(new Error('Invalid search type'));
    }
  }

  addOrReplaceCoordinatesMarker(latlng: LatLng): void {
    this.removeCoordinatesMarker();
    this.currentMarker = createDuplicateSearchMarker(latlng).addTo(
      this.mapService.map,
    );
  }

  bindDuplicateSearchCoordinatesMarker(
    formValues$: Observable<DuplicateSearchCoordinatesForm>,
    destroyRef: DestroyRef,
  ): void {
    formValues$
      .pipe(
        map((value) => this.getLatLngFromCoordinatesForm(value)),
        distinctUntilChanged((a, b) => {
          if (a === null && b === null) return true;
          if (a === null || b === null) return false;
          return a.equals(b);
        }),
        catchError((_, caught) => {
          this.removeCoordinatesMarker();
          return caught;
        }),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe((coordinates) => {
        if (coordinates) {
          this.addOrReplaceCoordinatesMarker(coordinates);
          return;
        }

        this.removeCoordinatesMarker();
      });
  }

  removeCoordinatesMarker() {
    if (this.currentMarker) {
      this.currentMarker.removeFrom(this.mapService.map);
      const marker = this.currentMarker;
      this.currentMarker = null;
      return marker;
    }
    return null;
  }

  private goToResults() {
    return this.router.navigate(
      [
        this.mapRoot,
        {
          outlets: {
            [RouteUtilsService.MAP_MENU_OUTLET_NAME]: [
              RouteUtilsService.MAP_MENU_ROOT_PATH,
              'vicnames',
              'polygon-search',
              'feature',
            ],
          },
        },
      ],
      {
        queryParamsHandling: 'preserve',
      },
    );
  }

  resumePolygonSearch() {
    // Don't restore the polygon object - it's stale
    // Just navigate and let the resolver use the stored GeoJSON
    return this.goToResults();
  }

  readonly lastPolygonGeoJson = signal<string | null>(null);
  readonly polygonSearch = signal<Polygon | null>(null);
  startPolygonSearch() {
    const mapMode = this.mapService.switchMapMode(VicnamesPolygonSearchMode, {
      shape: 'Polygon',
      allowSelfIntersection: false,
    });

    const sub = this.mapService.scaleFactor$
      .pipe(
        filter((scale) => scale > this.POLYGON_SEARCH_ALLOWED_THRESHOLD),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.toast.showErrorMessage('Zoomed out too far to use polygon search');
        this.mapService.resetMapMode();
        this.polygonSearch()?.remove();
        this.polygonSearch.set(null);
      });

    mapMode.shape$
      .pipe(
        tap((e) => {
          const polygon = e.layer as Polygon;
          const geoJsonStr = JSON.stringify(polygon.toGeoJSON().geometry);

          this.polygonSearch.set(polygon);
          // Store GeoJSON immediately so it's available after polygon is removed from map
          this.lastPolygonGeoJson.set(geoJsonStr);
        }),
        switchMap(() => {
          return this.goToResults();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        complete: () => {
          this.polygonSearch()?.remove();
          this.polygonSearch.set(null);
          sub.unsubscribe();
        },
      });
  }

  private getLatLngFromCoordinatesForm(
    value: DuplicateSearchCoordinatesForm,
  ): LatLng | null {
    if (!value.latitude || !value.longitude) {
      return null;
    }

    const latitude = convertDMSStringToDecimal(value.latitude);
    const longitude = convertDMSStringToDecimal(value.longitude);

    if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
      return null;
    }

    // Duplicate searches store Australia latitudes as positive DMS values.
    return latLng(-latitude, longitude);
  }
}
